import { TokenWithValue } from '@/domain/common/types'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { useConfettiContext } from '@/ui/molecules/confetti/Confetti'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { useEffect } from 'react'
import { SwapMode } from '../logic/types'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'

export interface SwapSuccessViewProps {
  runConfetti: boolean
  fromToken: TokenWithValue
  toToken: TokenWithValue
  swapMode: SwapMode
  resetForm: () => void
  isModal: boolean
}

export const swapModeToVerb: Record<SwapMode, string> = {
  swap: 'Swapped',
  wrap: 'Wrapped',
  unwrap: 'Unwrapped',
}

export function SwapSuccessView({
  fromToken,
  toToken,
  swapMode,
  runConfetti,
  resetForm,
  isModal,
}: SwapSuccessViewProps) {
  const { runAnimation } = useConfettiContext()

  useEffect(() => {
    if (runConfetti) {
      runAnimation()
    }
  }, [runConfetti, runAnimation])

  return (
    <PageLayout className={cn('pt-8 ', isModal ? 'mt-0' : 'xl:pt-28')}>
      <div className="flex flex-col items-center justify-center">
        <SuccessViewCheckmark isModal={isModal} />

        <Panel className="mt-8 min-w-full max-w-3xl">
          <Panel.Header>
            <Panel.Title className="text-xl">Summary</Panel.Title>
          </Panel.Header>

          <Panel.Content>
            <div className={cn('mt-4 flex flex-col gap-9', 'md:flex-row')}>
              <div className="flex grow flex-col" data-testid={testIds.easyBorrow.success.deposited}>
                <Typography variant="prompt" className="mb-2 font-semibold text-primary">
                  {swapModeToVerb[swapMode]}
                </Typography>

                <Item key={fromToken.token.symbol} {...fromToken} />
              </div>

              <div className="flex grow flex-col" data-testid={testIds.easyBorrow.success.borrowed}>
                <Typography variant="prompt" className="mb-2 font-semibold text-primary">
                  Received
                </Typography>

                <Item key={toToken.token.symbol} {...toToken} approx />
              </div>
            </div>

            <div className="mt-8 flex gap-4">
              <Button className="flex-1 bg-white/10 text-primary" onClick={resetForm} rounded="full">
                Close
              </Button>
            </div>
          </Panel.Content>
        </Panel>
      </div>
    </PageLayout>
  )
}

function Item({ token, value, approx = false }: TokenWithValue & { approx?: boolean }) {
  return (
    <div className="flex items-center border-t">
      <TokenIcon token={token} className="h-6 w-6" />

      <Typography className="ml-2 font-primary">{token.symbol}</Typography>

      <div className="flex grow flex-col py-3 pr-4">
        <Typography className="text-right font-primary">
          {approx && <span className="mr-1">~</span>}
          {token.format(value, { style: 'auto' })}
        </Typography>
        <Typography variant="prompt" className="text-right">
          {approx && <span className="mr-1">~</span>}
          {token.formatUSD(value)}
        </Typography>
      </div>
    </div>
  )
}
